import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Pagination from '@mui/material/Pagination';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import InputAdornment from '@mui/material/InputAdornment';
import ScenarioCard from '../../components/training/ScenarioCard';
import ScenarioDetailsDialog from '../../components/training/ScenarioDetailsDialog';
import { useTraining } from '../../context/TrainingContext';
import trainingApi from '../../services/trainingApi';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

const ScenarioLibrary = () => {
  const navigate = useNavigate();
  const { user } = useTraining();
  const isAdmin = !!user?.role?.includes('admin');
  const isLecturer = isAdmin || !!user?.role?.includes('lecturer') || !!user?.role?.includes('training_admin');
  // Lecturers see their own group's scenarios and, separately, the Platform Library to
  // copy from. Admins also get everything at once.
  const [source, setSource] = useState(isAdmin ? 'all' : 'mine');
  const [scenarios, setScenarios] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    difficulty: '',
    search: '',
  });
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [existingSessionConflict, setExistingSessionConflict] = useState(null);
  const [pendingScenarioId, setPendingScenarioId] = useState(null);
  const [selectedScenarioId, setSelectedScenarioId] = useState(null);
  const [platformGroupId, setPlatformGroupId] = useState(null);

  // The Platform Library's id, for the card badge (lecturers and admins only).
  useEffect(() => {
    if (!isLecturer) return undefined;
    let cancelled = false;
    trainingApi.getAuthorGroups()
      .then(response => {
        if (cancelled) return;
        const body = response.data || response;
        const data = body.data || body;
        if (data.libraryGroupId) setPlatformGroupId(data.libraryGroupId);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isLecturer]);

  const fetchScenarios = useCallback(async (currentFilters, page = 1) => {
    try {
      setIsLoading(true);
      const params = { page, limit: 20, sort: 'name' };
      if (isLecturer && source !== 'all') params.source = source;
      if (currentFilters.category) params.category = currentFilters.category;
      if (currentFilters.difficulty) params.difficulty = currentFilters.difficulty;
      if (currentFilters.search) params.search = currentFilters.search;

      const response = await trainingApi.listScenarios(params);
      const body = response.data || response;
      const data = body.data || body;
      setScenarios(data.scenarios || []);
      setPagination(data.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
    } catch (err) {
      console.error('[ScenarioLibrary] Error loading scenarios:', err);
      setError('Failed to load scenarios');
    } finally {
      setIsLoading(false);
    }
  }, [isLecturer, source]);

  // Initial load
  useEffect(() => {
    fetchScenarios(filters, 1);
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchScenarios(filters, 1);
    }, 300);
    return () => clearTimeout(timer);
  }, [filters, fetchScenarios]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handlePageChange = (_, page) => {
    fetchScenarios(filters, page);
  };

  // "Use in my group": copy a Library scenario (with its own persona) into the
  // lecturer's group, then open the copy to edit. It starts hidden from trainees.
  const handleCopy = async (scenarioId) => {
    try {
      const response = await trainingApi.copyScenario(scenarioId);
      const body = response.data || response;
      const copy = body.data || body;
      navigate(`/training/scenarios/${copy._id}/edit`);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not copy this scenario');
    }
  };

  const handleToggleAccess = async (scenarioId, studentAccess) => {
    setScenarios(prev => prev.map(sc => (sc._id === scenarioId ? { ...sc, studentAccess } : sc)));
    try {
      await trainingApi.updateScenario(scenarioId, { studentAccess });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not change who can see this scenario');
      fetchScenarios(filters, pagination.page);
    }
  };

  const handleOpenDetails = (scenarioId) => {
    setSelectedScenarioId(scenarioId);
  };

  const handleStartSession = async (scenarioId) => {
    setPendingScenarioId(scenarioId);
    setSelectedScenarioId(null);
    try {
      const response = await trainingApi.startSession(scenarioId);
      const body = response.data || response;
      const data = body.data || body;
      const session = data.session || data;
      navigate(`/training/session/${session._id}`);
    } catch (err) {
      const errData = err.response?.data || {};
      if (errData.existingSessionId || err.message?.includes('in progress')) {
        const sessionId = errData.existingSessionId;
        const scenarioName = errData.existingScenarioName || 'a scenario';
        setExistingSessionConflict({ sessionId, scenarioName });
      } else {
        setError(err.message || 'Failed to start session');
      }
    }
  };

  // Lecturer test-run: start the scenario in lecturer_test mode to verify it
  // behaves as designed. Mirrors handleStartSession but flags the mode so the
  // session bypasses grading and stays out of the student review queue.
  const handleTestRun = async (scenarioId) => {
    setPendingScenarioId(scenarioId);
    setSelectedScenarioId(null);
    try {
      const response = await trainingApi.startSession(scenarioId, { mode: 'lecturer_test' });
      const body = response.data || response;
      const data = body.data || body;
      const session = data.session || data;
      navigate(`/training/session/${session._id}`);
    } catch (err) {
      const errData = err.response?.data || {};
      if (errData.existingSessionId || err.message?.includes('in progress')) {
        setExistingSessionConflict({
          sessionId: errData.existingSessionId,
          scenarioName: errData.existingScenarioName || 'a scenario',
        });
      } else {
        setError(err.message || 'Failed to start test run');
      }
    }
  };

  const handleReturnToSession = () => {
    if (existingSessionConflict?.sessionId) {
      navigate(`/training/session/${existingSessionConflict.sessionId}`);
    }
    setExistingSessionConflict(null);
  };

  const handleAbandonAndStart = async (scenarioId) => {
    if (!existingSessionConflict?.sessionId) return;
    try {
      await trainingApi.abandonSession(existingSessionConflict.sessionId);
      setExistingSessionConflict(null);
      // Retry starting the new scenario
      handleStartSession(scenarioId);
    } catch (err) {
      setError('Failed to abandon existing session: ' + (err.message || ''));
      setExistingSessionConflict(null);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box>
          <Typography variant="h4" gutterBottom>Scenario Library</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Browse training scenarios. Select one to start a practice session.
          </Typography>
        </Box>
        {isLecturer && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<AutoFixHighIcon />}
              onClick={() => navigate('/training/scenarios/wizard')}
            >
              Wizard
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/training/scenarios/new')}
            >
              Create New
            </Button>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Existing session conflict dialog */}
      <Dialog open={!!existingSessionConflict} onClose={() => setExistingSessionConflict(null)}>
        <DialogTitle>Session Already in Progress</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            You have an active session with: <strong>{existingSessionConflict?.scenarioName}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You can return to your existing session, or cancel it and start a new one.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExistingSessionConflict(null)}>
            Close
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => handleAbandonAndStart(pendingScenarioId)}
          >
            Cancel Existing and Start New
          </Button>
          <Button
            variant="contained"
            onClick={handleReturnToSession}
          >
            Return to Existing Session
          </Button>
        </DialogActions>
      </Dialog>

      {/* Scenario details dialog */}
      <ScenarioDetailsDialog
        open={!!selectedScenarioId}
        onClose={() => setSelectedScenarioId(null)}
        scenarioId={selectedScenarioId}
        onStart={handleStartSession}
        onTestRun={isLecturer ? handleTestRun : undefined}
      />

      {isLecturer && (
        <Tabs value={source} onChange={(_, v) => setSource(v)} sx={{ mb: 2 }}>
          {isAdmin && <Tab value="all" label="All scenarios" />}
          <Tab value="mine" label={isAdmin ? 'Training groups' : 'My group'} />
          <Tab value="library" label="Platform Library" />
        </Tabs>
      )}
      {isLecturer && !isAdmin && source === 'library' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Scenarios shared with every training group. Choose "Use in my group" to get your own copy you can change and give to trainees.
        </Alert>
      )}

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={filters.category}
              label="Category"
              onChange={(e) => handleFilterChange('category', e.target.value)}
            >
              <MenuItem value="">All Categories</MenuItem>
              <MenuItem value="mood_disorders">Mood Disorders</MenuItem>
              <MenuItem value="anxiety_disorders">Anxiety Disorders</MenuItem>
              <MenuItem value="psychotic_disorders">Psychotic Disorders</MenuItem>
              <MenuItem value="personality_disorders">Personality Disorders</MenuItem>
              <MenuItem value="substance_use">Substance Use</MenuItem>
              <MenuItem value="neurodevelopmental_disorders">Neurodevelopmental</MenuItem>
              <MenuItem value="trauma_and_stress">Trauma &amp; Stress</MenuItem>
              <MenuItem value="eating_disorders">Eating Disorders</MenuItem>
              <MenuItem value="liaison_psychiatry">Liaison Psychiatry</MenuItem>
              <MenuItem value="child_and_adolescent">Child &amp; Adolescent</MenuItem>
              <MenuItem value="older_adult">Older Adult</MenuItem>
              <MenuItem value="forensic">Forensic</MenuItem>
              <MenuItem value="specialist">Specialist</MenuItem>
              <MenuItem value="cross_cutting_skills">Cross-Cutting Skills</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Difficulty</InputLabel>
            <Select
              value={filters.difficulty}
              label="Difficulty"
              onChange={(e) => handleFilterChange('difficulty', e.target.value)}
            >
              <MenuItem value="">All Levels</MenuItem>
              <MenuItem value="beginner">Beginner</MenuItem>
              <MenuItem value="intermediate">Intermediate</MenuItem>
              <MenuItem value="advanced">Advanced</MenuItem>
              <MenuItem value="expert">Expert</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Search scenarios..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
        </Box>
      </Paper>

      {/* Loading */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Scenario grid */}
      {!isLoading && scenarios.length === 0 && (
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No scenarios found matching your filters.
        </Typography>
      )}

      {!isLoading && scenarios.length > 0 && (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 2,
              mb: 3,
            }}
          >
            {scenarios.map(scenario => (
              <ScenarioCard
                key={scenario._id}
                scenario={scenario}
                platformGroupId={platformGroupId}
                onSelect={handleOpenDetails}
                onEdit={isLecturer ? (id) => navigate(`/training/scenarios/${id}/edit`) : undefined}
                onCopy={isLecturer ? handleCopy : undefined}
                onToggleAccess={isLecturer ? handleToggleAccess : undefined}
              />
            ))}
          </Box>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={pagination.pages}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ScenarioLibrary;
